import React from "react";
import { AlertTriangle, Lock } from "lucide-react";
import BusinessUnitMatcher from "../BusinessUnitMatcher/BusinessUnitMatcher";
import ConfirmTradeMappingsDialog from "../ConfirmTradeMappingsDialog/ConfirmTradeMappingsDialog";
import { ServiceTitanIntegrationState } from "@/modules/hub/features/CompanyManagement/hooks/useServiceTitanIntegration";

interface ServiceTitanBusinessUnitMappingProps {
  state: ServiceTitanIntegrationState;
}

const ServiceTitanBusinessUnitMapping: React.FC<
  ServiceTitanBusinessUnitMappingProps
> = ({ state }) => {
  const {
    allDoNotImport,
    businessUnitsError,
    businessUnitsLoading,
    changedMappingSummary,
    credential,
    handleConfirmSave,
    handleMappingChange,
    hasChanges,
    incompleteMappings,
    isConfirmModalOpen,
    isSavingMappings,
    mappings,
    setIsConfirmModalOpen,
    trades,
    tradesError,
    tradesLoading,
  } = state;

  return (
    <div>
      {tradesError && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium text-red-800 dark:text-red-300">
                Trade Loading Error:
              </span>
              <span className="text-red-700 dark:text-red-400 ml-1">
                {tradesError}
              </span>
            </div>
          </div>
        </div>
      )}
      {businessUnitsError && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium text-red-800 dark:text-red-300">
                Business Units Error:
              </span>
              <span className="text-red-700 dark:text-red-400 ml-1">
                {businessUnitsError}
              </span>
            </div>
          </div>
        </div>
      )}

      {incompleteMappings > 0 && (
        <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium text-yellow-800 dark:text-yellow-300">
                {incompleteMappings} business units need mapping:
              </span>
              <span className="text-yellow-700 dark:text-yellow-400 ml-1">
                Use the dropdown menus to assign each business unit to a trade
                category. For example, &quot;AC Maintenance&quot; maps to
                &quot;HVAC&quot; and &quot;Drain Cleaning&quot; maps to
                &quot;Plumbing&quot; if they primarily handle plumbing
                emergencies. Select &quot;Do not import&quot; to skip a business
                unit from syncing.
              </span>
            </div>
          </div>
        </div>
      )}

      {allDoNotImport && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium text-red-800 dark:text-red-300">
                At least one business unit must be mapped to a trade.
              </span>
              <span className="text-red-700 dark:text-red-400 ml-1">
                You cannot set all business units to &quot;Do not import&quot;.
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-auto ring-1 ring-gray-200 dark:ring-slate-700 md:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300 dark:divide-slate-700">
          <thead className="bg-gray-50 dark:bg-slate-700/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider">
                Business Unit
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider">
                Trade
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
            {businessUnitsLoading ? (
              <tr>
                <td
                  className="px-6 py-4 text-center text-sm text-gray-500 dark:text-slate-400"
                  colSpan={2}
                >
                  Loading business units...
                </td>
              </tr>
            ) : tradesLoading ? (
              <tr>
                <td
                  className="px-6 py-4 text-center text-sm text-gray-500 dark:text-slate-400"
                  colSpan={2}
                >
                  Loading trade options...
                </td>
              </tr>
            ) : mappings.length === 0 ? (
              <tr>
                <td
                  className="px-6 py-4 text-center text-sm text-gray-500 dark:text-slate-400"
                  colSpan={2}
                >
                  {credential?.tenantId
                    ? "No business units found for this tenant"
                    : "Configure ServiceTitan credentials to see business units"}
                </td>
              </tr>
            ) : (
              mappings.map((mapping) => (
                <BusinessUnitMatcher
                  businessUnitName={mapping.businessUnitName}
                  isLocked={mapping.isLocked}
                  key={mapping.id}
                  mappingId={mapping.id}
                  onMappingChange={handleMappingChange}
                  tradeId={mapping.tradeId}
                  trades={trades.map((trade) => ({
                    id: trade.id.toString(),
                    name: trade.name,
                  }))}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {mappings.length > 0 && (
        <div className="mt-4 flex justify-end">
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!hasChanges}
            onClick={() => setIsConfirmModalOpen(true)}
          >
            <Lock className="h-4 w-4 mr-2" />
            Save Mappings
          </button>
        </div>
      )}

      <ConfirmTradeMappingsDialog
        changedMappings={changedMappingSummary}
        isOpen={isConfirmModalOpen}
        isSaving={isSavingMappings}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </div>
  );
};

export default ServiceTitanBusinessUnitMapping;
